import { Icon } from '@ant-design/compatible';
import { Button, Checkbox, DatePicker, Input, Select } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DatasetFilters } from '../../types/dataset';

export const initialFilterValues: DatasetFilters = {
  name: '',
  tag: '',
  status: [],
  date: [],
  sort: [{ field: 'time_created', order: 'descend' }],
};

export const setFiltersOnMount = (query: URLSearchParams, setFilters: any, setPaginationObject: any) => {
  if (query) {
    setFilters((prev: DatasetFilters) => ({
      ...prev,
      name: query.get('name') || '',
      tag: query.get('tag') || '',
      status: query.get('status')?.split(',') || [],
      date:
        query.get('intervalStartDate') && query.get('intervalEndDate')
          ? [moment(query.get('intervalStartDate'), 'YYYY-MM-DD'), moment(query.get('intervalEndDate'), 'YYYY-MM-DD')]
          : [],
      sort:
        query
          .get('sort')
          ?.split(',')
          .map(s => ({ field: s.split(':')[0], order: s.split(':')[1] === 'ASC' ? 'ascend' : 'descend' })) || [],
    }));
  }
};

export const renderFilterString = (filters: DatasetFilters): string => {
  const queryParams = [];
  if (filters.name) {
    queryParams.push('name=' + filters.name);
  }
  if (filters.tag) {
    queryParams.push('tag=' + filters.tag);
  }
  if (filters.status.length) {
    queryParams.push('status=' + filters.status.map((f, i) => `${i !== 0 ? ',' : ''}${f}`).join(''));
  }
  if (filters.date.length) {
    const startDate = filters.date[0].format('YYYY-MM-DD');
    const endDate = filters.date[1].format('YYYY-MM-DD');
    queryParams.push('intervalStartDate=' + startDate + '&intervalEndDate=' + endDate);
  }
  if (filters.sort.length) {
    queryParams.push(
      'sort=' +
        filters.sort.map((s, i) => {
          const order = s.order === 'ascend' ? 'ASC' : 'DESC';
          return `${s.field}:${order}`;
        })
    );
  }
  if (queryParams.length) {
    return '&' + queryParams.map((param, i, arr) => param + (i === arr.length - 1 ? '' : '&')).join('');
  } else {
    return '';
  }
};

export const NameFilter = ({ filters, setFilters }: { filters: DatasetFilters; setFilters: any }) => {
  const [value, setValue] = useState<string>(filters.name);

  useEffect(() => {
    setTimeout(() => {
      setFilters((prev: any) => ({ ...prev, name: value }));
    }, 400);
  }, [value, setFilters]);

  return (
    <div style={{ padding: '4px 0' }}>
      <Input
        style={{ margin: '8px 12px', width: 'auto' }}
        type="text"
        value={value}
        onChange={e => {
          e.persist();
          setValue(e.target.value);
        }}
      />
      <div style={{ padding: '7px 8px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
        <Button size="small" type="link" onClick={() => setValue('')}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export const TagFilter = ({ filters, setFilters }: { filters: DatasetFilters; setFilters: any }) => {
  const [value, setValue] = useState<string>(filters.tag);

  useEffect(() => {
    setTimeout(() => {
      setFilters((prev: any) => ({ ...prev, tag: value }));
    }, 400);
  }, [value, setFilters]);

  return (
    <div style={{ padding: '4px 0' }}>
      <Input
        style={{ margin: '8px 12px', width: 'auto' }}
        type="text"
        value={value}
        onChange={e => {
          e.persist();
          setValue(e.target.value);
        }}
      />
      <div style={{ padding: '7px 8px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
        <Button size="small" type="link" onClick={() => setValue('')}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export const StatusFilter = ({ filters, setFilters }: { filters: any; setFilters: any }) => {
  const toggleStatus = (e: any, status: string) => {
    if (e.target.checked) {
      setFilters((prev: any) => ({ ...prev, status: [...prev.status, status] }));
    } else {
      setFilters((prev: any) => ({ ...prev, status: [...prev.status.filter((v: any) => v !== status)] }));
    }
  };

  return (
    <div style={{ padding: '4px 0', display: 'flex', flexDirection: 'column' }}>
      {[
        {
          text: 'New',
          value: 'NEW',
        },
        {
          text: 'Ongoing',
          value: 'ONGOING',
        },
        {
          text: 'Done',
          value: 'DONE',
        },
        {
          text: 'Saved',
          value: 'SAVED',
        },
      ].map((status: { text: string; value: string }) => (
        <Checkbox
          style={{ padding: '5px 12px', marginLeft: 0 }}
          key={status.value}
          checked={filters.status.includes(status.value)}
          onChange={e => toggleStatus(e, status.value)}>
          {status.text}
        </Checkbox>
      ))}
      <div style={{ padding: '7px 8px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
        <Button type="link" size="small" onClick={() => setFilters((prev: any) => ({ ...prev, status: [] }))}>
          Reset
        </Button>
      </div>
    </div>
  );
};

export const DateFilter = ({ filters, setFilters }: { filters: any; setFilters: any }) => {
  const { RangePicker } = DatePicker;
  const { Option } = Select;

  const [datePreset, setDatePreset] = useState('');

  return (
    <div>
      <RangePicker
        style={{ padding: 9 }}
        format="YYYY-MM-DD"
        value={filters.date}
        onChange={e => {
          setFilters((prev: any) => ({ ...prev, date: e !== null ? e : [] }));
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid #e8e8e8',
          padding: '7px 8px',
        }}>
        <Select
          style={{ width: 150 }}
          placeholder="Select a preset"
          value={datePreset}
          onChange={(e: any) => {
            setDatePreset(e);
            setFilters((prev: any) => ({ ...prev, date: e ? [moment().subtract(e, 'months'), moment()] : [] }));
          }}>
          <Option value="">Select a preset</Option>
          <Option value="1">Last 1 month</Option>
          <Option value="2">Last 2 months</Option>
        </Select>
        <div>
          <Button
            type="link"
            size="small"
            style={{ width: 50, color: '#00b9ff' }}
            onClick={() => {
              setDatePreset('');
              setFilters((prev: any) => ({ ...prev, date: [] }));
            }}>
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SorterToggle = ({
  filterKey,
  label,
  filters,
  setFilters,
}: {
  filterKey: string;
  label: string;
  filters: any;
  setFilters: any;
}) => {
  return (
    <Button
      onClick={() => {
        if (!filters.sort.find((s: any) => s.field === filterKey)) {
          setFilters((prev: DatasetFilters) => ({
            ...prev,
            sort: [...prev.sort, { field: filterKey, order: 'ascend' }],
          }));
        } else if (filters.sort.find((s: any) => s.field === filterKey && s.order === 'descend')) {
          setFilters((prev: DatasetFilters) => ({
            ...prev,
            sort: prev.sort.filter(s => s.field !== filterKey),
          }));
        } else {
          setFilters((prev: DatasetFilters) => ({
            ...prev,
            sort: prev.sort.map(s => {
              if (s.field === filterKey) {
                return { field: s.field, order: 'descend' };
              } else {
                return s;
              }
            }),
          }));
        }
      }}
      className="flex justify-between items-center">
      {label}
      <div className="flex flex-col justify-center items-center ml-2">
        <Icon
          style={{
            marginBottom: '-5px',
            color: filters.sort.find((s: any) => s.field === filterKey && s.order === 'ascend') ? '#00b9ff' : '#ddd',
          }}
          type="caret-up"
        />
        <Icon
          style={{
            color: filters.sort.find((s: any) => s.field === filterKey && s.order === 'descend') ? '#00b9ff' : '#ddd',
          }}
          type="caret-down"
        />
      </div>
    </Button>
  );
};

export const createQueryStringFirstTimeForPageFetch = (query: URLSearchParams) => {
  const string =
    (query.get('name') ? `&name=${query.get('name')}` : '') +
    (query.get('tag') ? `&tag=${query.get('tag')}` : '') +
    (query.get('status') ? `&status=${query.get('status')}` : '') +
    (query.get('intervalStartDate') ? `&intervalStartDate=${query.get('intervalStartDate')}` : '') +
    (query.get('intervalEndDate') ? `&intervalEndDate=${query.get('intervalEndDate')}` : '') +
    (query.get('sort') ? `&sort=${query.get('sort')}` : '');
  return string;
};
